import ScopsImage from '../../../assets/images/insights/emissions.svg';

const insightPageData = {
  header: {
    image: {
      name: "insight-page-header.jpg",
      alt: "Insight page header background",
    },
    title: "Insights",
  },
  articles: [
    {
      title : "Financed emissions 101",
      shortDescription : "Financial institutions have a significant responsibility in funding the transition to a low-carbon economy. They can alleviate the worst effects of climate change by reallocating and decarbonising their portfolios and investing in climate mitigation and adaptation measures.",
      content : 
      <>
        <p>Financial institutions have a significant responsibility in funding the transition to a low-carbon economy. They can alleviate the worst effects of climate change by reallocating and decarbonising their portfolios and investing in climate mitigation and adaptation measures.</p>
        <p>In addition to managing greenhouse gas (GHG) emissions created from their operational activities, financial institutions are also accountable for financed emissions. These are the emissions related to loans, underwriting, investments, and other financial services that the bank may provide.</p>
        <p>According to the Carbon Disclosure Project's (CDP) The Time to Green Finance report<sup>1</sup> , financed emissions account for 700 times more than a financial institution's directly generated emissions. </p>
        <h2>What are Financed emissions?</h2>
        <p>Financed emissions are emissions generated as a result of financial services, investments, and lending by investors and companies that provide financial services. They fall under scope 3, category 15 from the Greenhouse Gas Protocol (GHGP).</p>
        <p>One of the Paris Agreement aims is “making finance flows consistent with low GHG emissions and a climate-resilient pathway." Financial institutions are major capital providers and hence have a key role in decarbonising the global economy. </p>
        <p>Financed emissions are important because they contribute a considerable portion of global GHG emissions. A recent report from Sierra Club found that the 18 largest U.S. banks and asset managers were responsible for financing the equivalent of 1.97 billion tons of CO<sub>2</sub>eq in 2020. This would make the U.S. financial sector the fifth largest global emitter if it were a country, just behind Russia.</p>
        <p>The CDP’s The Time to Green Finance report also found that only 25% of financial organisations measure financed emissions, while 49% don’t analyze their portfolio’s climate impact. This leaves a sizable blind spot in the global emissions accounting sheet. Unaccounted financed emissions put the global climate at risk while exposing financiers to reputational and financial risks. Alternatively, when financed emissions are properly accounted for, it gives banks and asset managers an understanding of their products and services’ climate costs.</p>
        <h2>Challenges when reporting Financed emissions</h2>
        <p>The World Resources Institute’s (WRI) Banking Beyond Climate Commitments paper highlighted the following challenges:</p>
        <ul>
          <li>Considerable time, money, and resources are needed to assess and develop needed data</li>
          <li>Lack of standardized legislation, policies, methodologies, and tools when measuring impact</li>
          <li>Scrutiny over the additionality and fungibility of green bonds</li>
          <li>Limitations from governments and financial regulators that hinder the progress of Paris-aligned decision-making</li>
          <li>Firms’ participation in greenwashing as a result of difficulty creating standards that apply to all sectors</li>
        </ul>
        <p>These difficulties hinder financial institutions from creating comprehensive and accurate reports. Oftentimes, information is incomplete, estimated, or has other issues that do not capture the holistic. This can stem from an inability to get complete information from companies in a firm’s portfolio. These companies may not have high-quality data available or may not collect enough usable emissions information for reporting. Difficulties with data collection, standardized data sources, and policies can also result in missed goals that were overly ambitious or incomparable disclosures between firms.</p>
        <h2>Race to Zero</h2>
        <p>The United Nations Framework Convention on Climate Change (UNFCCC) manages the global Race To Zero campaign. It rallies support from all levels and leads net zero initiatives. The Glasgow Financial Alliance for Net Zero (GFANZ) aligns its goals with the Race To Zero</p>
        <h2>Glasgow Financial Alliance for Net Zero (GFANZ)</h2>
        <p>GFANZ’s goal is to unify net zero worldwide financial industry-specific alliances into one. There are over 450 members representing more than $130 trillion in assets.</p>
        <p>The following alliances and initiatives are all part of GFANZ:</p>
        <ul className="dash-list">
          <li>- Net-Zero Banking Alliance (NZBA)</li>
          <li>- Net Zero Asset Managers Initiative (NZAMI)</li>
          <li>- Net-Zero Asset Owner Alliance (NZAOZ)</li>
          <li>- Paris Aligned Investment Initiative (PAII)</li>
          <li>- Net-Zero Insurance Alliance (NZIA)</li>
          <li>- Net Zero Financial Service Providers Alliance (NZFSPA)</li>
          <li>- Net Zero Investment Consultants Initiative (NZICI)</li>
        </ul>
        <p>GFANZ and the alliances and initiatives under its umbrella align with Race To Zero criteria. The members of these groups commit to transitioning their portfolios to net zero, supporting investing aligned with net zero emissions, and supporting the overall goal of achieving net zero GHG emissions by 2050. </p>
        <h2>PCAF and its role in emission disclosure</h2>
        <p>The Partnership for Carbon Accounting Financials (PCAF) is a global collaboration of financial institutions that have developed a framework to help firms measure financed emissions. It was created in 2015 in response to a growing need for fully accountable and comparable financed emissions calculations. Scope 3, category 15 was a precursor to PCAF.</p>
        <p>There are nearly 300 PCAF members who've committed to measuring and disclosing their financed emissions. They aim to create and implement a streamlined approach to disclosing GHG emissions resulting from investments and loans.</p>
        <p>The PCAF framework achieves this by providing formulas for allocating the carbon impacts for transactions in six major asset classes. This ensures that financed emissions calculations are comparable across the whole finance industry. PCAF also aligns with the CDP, Science Based Targets initiative (SBTi), and the Task Force on Climate-Related Financial Disclosures (TCFD) to streamline reporting and complement guidelines from those respective initiatives. Eventually, the goal is for financial institutions to also make changes in their portfolio to reduce emissions and align with the Paris Agreement.</p>
        <p>Financed emissions are calculated by looking at an activity's carbon footprint and allocating that footprint to the financial institution. The accounting process for each loan, investment, or debt is conducted in two parts:</p>
        <ul>
          <li>Estimating the total carbon footprint of any activity within a loan, investment, or financial service</li>
          <li>Allocating that carbon footprint to the financial institution through a shared attribution calculation</li>
        </ul>
        <p>PCAF developed the Global GHG Accounting and Reporting Standard for the Financial Industry to give firms more guidance on reporting on financed emissions. PCAF started with guidance on the following asset classes:</p>
        <br />
        <br />
        <div className="table-container">
          <table className="border-bottom-table rtl">
              <tr>
                  <th>Asset Class</th>
                  <th>Method of calculation</th>
              </tr>
              <tr>
                <td>Listed equity and Corporate Bonds</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Company emissions</span>
                </td>
              </tr>
              <tr>
                <td>Commercial real Estate</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Building emissions</span>
                </td>
              </tr>
              <tr>
                <td>Business Loan and Unlisted equity</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Company emissions</span>
                </td>
              </tr>
              <tr>
                <td>Mortgages</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Building emissions</span>
                </td>
              </tr>
              <tr>
                <td>Motor Vehicle Loans</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Vehicle emissions</span>
                </td>
              </tr>
              <tr>
                <td>Project Finance</td>
                <td className="math-equ">
                  <div className="fraction">
                    <span className="num">Outstanding amount</span>
                    <span className="dem">Total company equirt + debt</span>
                  </div>
                  <span className="secon-part">&times; Building emissions</span>
                </td>
              </tr>
          </table>
          </div>
          <br />
          <br />
          <h2>How Can Firms Start to Manage Financed Emissions?</h2>
          <p>Financial institutions should keep key stakeholders informed and educated, create actionable steps to decarbonise their portfolio, and continually improve financed emission plans. This isn't an exhaustive list, but setting down a strong foundation and getting buy-in can give financial institutions a strong start as they begin working toward their targets.</p>
          <p>These are some steps firms should take in addition to setting targets:</p>
          <ul>
            <li>Determine key stakeholders needed to reach key targets, their roles, and what actions they need to take.</li>
            <li>Create interim targets and build in checkpoints to keep a pulse on progress.</li>
            <li>Create interim targets and build in checkpoints to keep a pulse on progress.</li>
            <li>Invest in training for financial advisers, asset managers, and others who will work with customers and portfolio companies.</li>
            <li>Engage with high-emissions portfolio companies and customers to encourage them to begin measuring and/or lowering emissions.</li>
            <li>Prioritise tackling the biggest emissions “hot spots” first to make the biggest impact.</li>
            <li>Research “green” investing opportunities that can help decarbonise your portfolio, like renewable energy and transition bonds.</li>
            <li>Revisit and revise targets as needed when more data becomes available, and more companies begin disclosing.</li>
            <li>Create feedback processes to learn how the implementation is going and how it can be improved.</li>
            <li>Dedicate resources to keep track of changing legislation affecting your portfolio companies and customers.</li>
          </ul>
      </>,
      image: {
        name: "Financed-emissions.jpg",
        alt: "Image two description",
      },
    },
    {
      title : "Towards environmental sustainability: How SAB is taking action",
      shortDescription : "The Kingdom of Saudi Arabia (KSA) reaffirmed its commitment to the Paris Agreement goals on 23 October 2021",
      content :
      <>
          <h2>Introduction</h2>
          <p>The Kingdom of Saudi Arabia (KSA) reaffirmed its commitment to the Paris Agreement goals on 23 October 2021<sup>1</sup>. KSA raised its environmental and climate contribution by committing to reducing, avoiding and removing 278 million tonnes of CO<sub>2</sub>eq in greenhouse gas (GHG) emissions annually by 2030<sup>2</sup>. In 2020 the Kingdom’s oil and gas products made up 69% of its exports<sup>3</sup>, however its Vision 2030 outlines how the Kingdom will diversify the economy and prioritise the conservation and protection of native plants and animals, among other focus areas. KSA is already working towards this vision by investing in renewable energy, green hydrogen, and carbon sequestration.</p>
          <p>SAB is one of the largest banks in Saudi Arabia and supports all sectors of the Kingdom’s economy. We understand that this position of influence brings the responsibility to lead the charge in terms of environmental sustainability and to promote the Kingdom’s Net Zero ambitions across various sectors.</p>
          <p>Environmental sustainability is built into SAB’s strategy, with the following key milestones guiding our way:</p>
          <ul>
              <li>  2025: Achieve sustainable financing and investments of SAR 34 billion</li>
              <li>  2035 (or sooner): Achieve Net Zero in our operations </li>
              <li>  2060 (or sooner): Achieve Net Zero in our financed emissions</li>
          </ul>
          <p>In this article, we outline our three focus areas toward achieving these targets and supporting Vision 2030.</p>
          <h2>1. Sustainable finance and investments</h2>
          <p>Our commitment to environmental protection is exemplified through sustainable financing, enabling our clients to transition to a low carbon economy. SAB has pledged to deploy SAR 34 billion in sustainable financing by 2025. This entails increasing our ESG compliant lending to comprise 15% to 20% of our total asset book, setting a notable benchmark for other banks in KSA.</p>
          <p>SAB has made remarkable strides, and we are pleased with the progress achieved. In 2021, we introduced an innovative climate transition fund, directing investments towards equities that mitigate the impact of climate change. Building on this success, in 2022, we launched a fully Islamic and Shariah-compliant ESG green deposit, empowering retail clients to invest in environmentally friendly financing and socially beneficial initiatives. The strong response from our customers is evident, as they constitute over 10% of our total product count for Islamic term deposits. Notably, this offering has surpassed our legacy Islamic term product in terms of growth. These results demonstrate the existing market demand and our expectation for investors to expand their investment scope, aligning with our objective of promoting sustainable banking products.</p>
          <p>We take great pride in serving as one of the mandated lead arrangers for the SAR 14 billion financing raised to support the Red Sea Project in March 2021. The Red Sea Project, a remarkable sustainable development initiative aligned with Vision 2030, prioritises renewable energy and regenerative tourism. Our partner, HSBC, assumed the role of green loan coordinator for this project. Our active participation in the Red Sea transaction led to SAB being honoured as the Best Bank for Sustainable Finance in Saudi Arabia by Global Finance Magazine in 2022.</p>
          <p>Furthermore, we are actively collaborating with the government and other stakeholders in the investment market to foster discussions on sustainable finance. SAB played a significant role in the Kingdom's Public Investment Fund's inaugural green bond issuance, marking a noteworthy milestone as the first green bond issuance by a sovereign wealth fund.</p>
          <h2>2. Achieving Net Zero in our operations by 2035</h2>
          <p><b>Green initiatives: Waste, water and emissions </b></p>
          <p>SAB’s internal greening initiatives included eliminating the use of plastic bottles at our offices, reducing travel emissions, saving water and energy, and reducing paper consumption by digitising our processes and systems. In 2022 we were able to report the following outcomes: </p>
          <br /><br />
          <div className="table-container">
          <table>
              <tr>
                  <th>2.6 million</th>
                  <th>374.9 kg</th>
                  <th>1.7 million kWh</th>
                  <th>221.884 litres</th>
              </tr>
              <tr>
                  <td>Single-use plastic water bottles saved</td>
                  <td>Waste recycled</td>
                  <td>Energy saved</td>
                  <td>Water saved</td>
              </tr>
          </table>
          </div>
          <br /><br />
          <p>SAB was the first KSA bank to eliminate plastic straws and single-use plastic bottles across all its office buildings. Furthermore, we estimate that the 374,926 kg of waste we recycled in 2022, as part of a recycling programme rolled out across four of our buildings and a digitisation initiative at our document warehouse, contributed to saving: </p>
          <br /><br />
          <div className="table-container">
          <table>
              <tr>
                  <td>6,373 trees</td>
                  <td>937 m<sup>3</sup> landfill waste</td>
                  <td>9.9 million litres of water</td>
                  <td>2.2 million kWh energy</td>
              </tr>
          </table>
          </div>
          <br /><br />
          <p>Our recycling project will be rolled out across all our branches and buildings during 2023.</p>
          <p>Concurrently, our energy reduction initiatives in 2022 included replacing old visual display monitors with monitors that are more energy efficient and installing sensors at our buildings to switch off unnecessary lights. We also renovated our water infrastructure, installed motion-detecting infrared taps and upgraded our flushing toilet systems.</p>
          <p><b>SAB’s new smart office</b></p>
          <p>In Q3 2023, we will move into our new LEED-certified headquarters. The new building incorporates smart technology into its environmentally friendly design, to support our journey towards Net Zero by providing an estimated:</p>
          <br /><br />
          <div className="table-container">
          <table>
              <tr>
                  <th>28.47%</th>
                  <th>59.25%</th>
                  <th>54.3%</th>
              </tr>
              <tr>
                <td>Reduction in operational energy consumption</td>
                <td>Reduction in water consumption for landscaping</td>
                <td>reduction in indoor water consumption</td>
              </tr>
          </table>
          </div>
          <br /><br />
          <p>Sustainability is built into the very fabric of the new office, in that recycled content makes up 28% our procured construction materials and 75% of the construction waste was recycled. We look forward to showing off the state-of-the-art, sustainable premises to our customers and employees.</p>
          <p><b>Tracking our emissions</b></p>
          <p>We are measuring our baseline carbon footprint in our operations and supply chain. In 2022, as part of an externally facilitated process, SAB identified the following sources of our direct and indirect emissions:</p>
          <img 
            src={ScopsImage} 
            alt={"ESG"} 
          />
          <p>In 2022, we initiated a comprehensive assessment of SAB's Scope 1, 2, and 3 emissions (excluding Financed Emissions), utilising data from 2021 as part of a benchmarking initiative. The purpose of this exercise was to enhance our organisational structure in preparation for the GHG baseline work scheduled for 2023, utilising data from 2022. For further information regarding our progress and the results of the review, please visit [link].</p>
          <p>We have consciously decided to review SAB’s financed emissions during 2023, as our intention was to build internal capacity and appropriate capabilities before undertaking this exercise which requires a significant amount of investment in terms of resources and costs.</p>
          <h2>3. Impact based partnerships for carbon removal</h2>
          <p>The third pillar of SAB’s environmental sustainability approach comprises collaborating with specialist partners as the Kingdom journeys towards a Net Zero future.</p>
          <p><b>Planting 1 million trees </b></p>
          <p>The Saudi Green Initiative (SGI) oversees the Kingdom’s work in protecting natural areas, promoting afforestation to offset global emissions, and fostering sustainable economic activities. Part of its strategy is to plant 10 billion trees in the Kingdom and rehabilitate 40 million hectares of land. In support of SGI, SAB has committed to planting 1 million trees by 2030 - partnering with the National Centre for Vegetation Cover (NCVC), the Ministry of Environment Water and Agriculture, and Marooj Foundation to meet this goal. We already planted 50,000 trees in 2022. Our strategy focuses on restoring the mangrove forests along the Red Sea and Arabian Gulf coasts, which not only combats climate change, but bolsters biodiversity and supports the local fishing industry by maintaining corals and marine life.</p>
          <p><b>Conserving native plantlife</b></p>
          <p>The SGI’s afforestation initiatives focus primarily on native wild plants, many of which face extinction. SAB has partnered with the Environmental Awareness Association to establish the Native Plant Conservation and Propagation Centre – a first in KSA. The purpose of the Centre is to preserve plant diversity by planting local trees and building infrastructure where native plants can be grown and studied. It will also provide viable native plant seeds and seedlings to researchers and authorities.</p>
          <p><b>Boosting environmental awareness</b></p>
          <p>SAB participates in events that raise awareness about the importance of protecting the environment through various initiatives, including the following:</p>
          <ul>
              <li>  Our employees and communities are encouraged to volunteer in the Riyadh Municipality’s desert cleaning campaign, as well as a beach cleaning campaign initiated by SAB in partnership with the Municipality of Al Khobar.</li>
              <li>  We observe the United Nations’ World Environment Day annually and switch off all non-essential lights for the World Wildlife Fund’s international “Earth Hour”. </li>
              <li>  We participate in the Saudi Wildlife Commission’s programmes, partly by funding research into humanity’s impacts on marine environments.</li>
          </ul>
          <p>SAB takes a holistic approach to preserving natural resources, safeguarding the environment, and combating climate change in line with Vision 2030 and international priorities. We encourage every person – every employee, every customer, every investor, and every partner – to stand with us to restore, reshape and protect our beautiful home Kingdom.</p>
          <h2>List of references:</h2>
          <ol>
              {/* <li><a target='_blank' href="https://unfccc.int/sites/default/files/NDC/2022-06/To%20UNFCCC%201st%20Updated%20NDC%20Submission%20letter.PDF">Link 1</a></li> */}
              <li><a target='_blank' href=" https://unfccc.int/documents/497886">Link 1</a></li>
              <li><a target='_blank' href="https://www.imf.org/en/Publications/CR/Issues/2021/07/07/Saudi-Arabia-2021-Article-IV-Consultation-Press-Release-and-Staff-Report-461736">Link 2</a></li>
          </ol>
      </>,
      image: {
        name: "Towards-environmental-sustainability-How-SAB-is-taking-action.jpg",
        alt: "Image two description",
      },
    },
    {
      title : "Climate change vs environmental protection",
      shortDescription : "The world is experiencing significant nature-related risks, including rising temperatures that change weather patterns and disrupted ecosystems that change the natural landscape. Nature-related risks now affect every country on every continent.",
      content : 
      <>
        <p>The world is experiencing significant nature-related risks, including rising temperatures that change weather patterns and disrupted ecosystems that change the natural landscape. Nature-related risks now affect every country on every continent.</p>
        <p>These risks also have severe consequences for biodiversity, agriculture, human health, and the economy. For example, extreme heatwaves and droughts can damage crops and reduce food supplies, leading to food insecurity and malnutrition. At the same time, the increase in global population is putting a strain on natural resources, and unsustainable practices such as deforestation, overfishing, and pollution are causing irreversible damage to the planet.</p>
        <p>It has been recognized that the harm we do to the planet and ecosystems is unsustainable. With the increased media coverage about the proliferation of unsustainable environmental practices and negative impacts associated with global warming, the general public at times struggle to differentiate between combating climate change and protecting the environment. In this article we hope to highlight the differences, but also the interconnectivity of these important earth systems.</p>
        <h2>Climate change vs environmental protection</h2>
        <p><a target='_blank' href="https://www.un.org/en/climatechange/what-is-climate-change">Climate change</a> refers to long-term shifts in temperatures and weather patterns<sup>1</sup> according to the United Nations. There are natural patterns of climate change, however the climate change that is causing concern is caused by the human activity culminating with the industrial revolution which started around 1760 AD. The anthropogenic impact of human activities has been substantial and intertwined with various advancements and improvements in society. Remarkable progress in medicine, hygiene practices, and reductions in the mortality rates of new-borns have resulted in increased life expectancy and population growth. These achievements have led to a greater number of people living longer lives, which, in turn, has sparked higher demands for resources and increased consumption of meat, energy, travel, goods, materials, and waste generation. This rapid increase in resource consumption and waste generation has strained the environment. The extraction of resources, such as fossil fuels and minerals, has resulted in environmental degradation, encompassing deforestation, habitat loss, and pollution. The excessive generation of waste, including plastic pollution, exacerbates the environmental challenges we face. The amount of greenhouse gases that we release into the atmosphere through our transport, manufacturing, mining and other activities are causing a global warming effect that change short-term weather events in unusual, and often destructive, ways.</p>
        <p><a target='_blank' href="https://www.iucn.org/resources/issues-brief/species-and-climate-change#:~:text=The%20Bramble%20Cay%20melomys%20(Melomys,destroyed%20by%20rising%20sea%20levels">Environmental protection</a> includes efforts by individuals, organisations and governments to conserve natural resources and the existing environment and, where possible, to repair damage and reverse negative trends. Pollution and the destruction of natural habitats are of grave concern.<br /><br /> It is important to understand that protecting the environment and combating climate change are two sides of the same coin. Global health systems are connected and strongly interlinked, and climate change leading to global warming has already led to the extinction of species that could not adapt quickly enough<sup>2</sup>, while the loss of some of the world’s great mangrove- and rainforests has sped up global warming. </p>
        <p>However, some experts argue that climate change is the more urgent issue, since it affects the entire planet and poses a threat to human survival. Others contend that safeguarding the environment is crucial because the environment is the primary source of natural resources, including food, water, and air. Ensuring that the environment remains healthy and sustainable means that natural resources will continue to be available to current and future generations.</p>
        <p>While there is no denying that both climate change and environmental protection are critical issues that require urgent attention, prioritising one over the other results in an inadequate approach to the  current crises. Instead, it requires a balanced solution that both protects the environment and mitigates the effects of climate change.</p>
        <p>The pace of change is quickening as more people prioritise measures that will increase the natural world’s resilience. Affordable and scalable solutions are now available to enable countries to leapfrog to cleaner, more sustainable practices, such as recycling and use of renewable energy sources that lower carbon emissions. The danger of a too-severe focus on one side of the coin over the other may result in, for instance, a rush to new energy sources such as wind and solar without properly understanding the impact that these may have on the natural environment.</p>
        <div className="border-box">
          <p>It is clear that climate change and environmental protection are both vital issues that need to be addressed simultaneously – they affect each other. Business leaders and governments must find ways to reduce carbon emissions while also protecting the planet. By working together, they can preserve the environment, mitigate the negative impacts of climate change and create a sustainable future for current and future generations.</p>
        </div>
        <h2>International frameworks promoting environmental sustainability</h2>
        <p>Governments, companies and institutions have realised the importance of Climate and the Environment, and we are seeing more and more companies publishing non-financial disclosures related to climate and nature. The Task Force on Nature-related Financial Disclosures (TNFD)<sup>3</sup> plays a crucial role in addressing nature-related risks and promoting sustainable practices. The TNFD, modelled after the successful Task Force on Climate-related Financial Disclosures (TCFD)<sup>4</sup> (which is a globally recognised body to have published disclosure guidelines on Climate related risks), aims to develop a framework for businesses and financial institutions to assess and disclose their impacts and dependencies on nature. By incorporating nature-related information into financial decision-making processes, the TNFD aims to enhance transparency, identify potential risks, and incentivise investments that are aligned with biodiversity conservation and ecosystem resilience. This initiative recognises that the loss of nature poses significant risks to the global economy, and by integrating nature-related considerations, it seeks to foster a more sustainable and resilient financial system.</p>
        <p>Similarly, the International Union for Conservation of Nature (IUCN)<sup>5</sup> is a globally recognised organisation dedicated to conserving nature and ensuring the sustainable use of natural resources. The IUCN's work focuses on assessing the conservation status of species, defining protected areas, and promoting sustainable practices. The organisation maintains the IUCN Red List, a comprehensive inventory of the conservation status of species worldwide. It also provides valuable guidance on ecosystem management and sustainable development through the development of standards, best practices, and policy recommendations. By leveraging scientific knowledge and collaborating with governments, NGOs, and local communities, the IUCN plays a vital role in mitigating risks such as biodiversity loss, habitat degradation, and unsustainable resource use, thereby contributing to the overall health and resilience of our planet's ecosystems.</p>
        <h2>References:</h2>
        <ol>
          <li><a target='_blank' href="https://www.un.org/en/climatechange/what-is-climate-change">Link 1</a></li>
          <li><a target='_blank' href="https://www.iucn.org/resources/issues-brief/species-and-climate-change#:~:text=The%20Bramble%20Cay%20melomys%20(Melomys,destroyed%20by%20rising%20sea%20levels">Link 2</a></li>
          <li><a target='_blank' href="https://tnfd.global/">Link 3</a></li>
          <li><a target='_blank' href="https://www.fsb-tcfd.org/">Link 4</a></li>
          <li><a target='_blank' href="https://www.iucn.org/ ">Link 5</a></li>
          <li>Outstanding amount&#x2044;Total company equity + debt</li>
        </ol>
      </>,
      image: {
        name: "Climate-change-vs-environmental-protection.jpg",
        alt: "Image two description",
      },
    },
    {
      title: "What is ESG investing?",
      shortDescription:
        "ESG stands for Environmental, Social, and Governance. It is a set of criteria used to evaluate a company's performance in areas related to sustainability, ethical behaviour, and social responsibility.",
      content:
        <>
          <p>
            ESG stands for Environmental, Social, and Governance. It is a set of
            criteria used to evaluate a company's performance in areas related
            to sustainability, ethical behaviour, and social responsibility. In
            recent years ESG matters have come increasingly into the spotlight
            when making investment decisions, giving rise to ESG investing as a
            distinct concept. ESG investors or funds evaluate a company's
            performance based on its environmental impact, social
            responsibility, and governance practices before committing funds to
            it.
          </p>
          <h2>Environment</h2>
          <p>
            As average temperatures and sea levels continue to rise, climate
            science finds that extreme weather events such as heat waves and
            floods will increase in frequency and severity. These will cause
            loss of life, productivity and livelihoods - as well as contributing
            to poverty and inequality. Extreme weather events will also damage
            property, infrastructure and ecological systems, compounded by the
            continued depletion of finite natural resources. Against this
            backdrop, investors are now demanding that their wealth is used in a
            way that contributes to a more sustainable world and to
            environmental protection. The environmental category measures how
            companies approach waste management and energy efficiency. This
            category also assesses the natural resources and reserves on which
            companies base their operations, as well as the environmental
            resources and processes they require to operate and produce their
            end products.
          </p>
          <p>
            Companies that fail to manage their environmental risks, such as
            pollution or climate change, can face increased regulatory costs,
            fines, and legal liabilities. These costs can eat into profits and
            damage a company's reputation, making it harder to attract customers
            and investors. Conversely, companies that take steps to reduce their
            environmental impact and adopt sustainable practices can enjoy cost
            savings from energy and resource efficiency, as well as increased
            customer loyalty and investor interest.
          </p>
          <h2>Social</h2>
          <p>
            The social category looks at how the company treats its employees,
            customers, suppliers and communities. Companies that fail to manage
            their social risks, such as worker safety or human rights abuses,
            can face negative publicity, lawsuits, and boycotts. These can lead
            to reputational damage, loss of sales, and increased costs
            associated with legal fees, remediation, and monitoring. On the
            other hand, companies that prioritise social responsibility and
            stakeholder engagement can build trust with customers, employees,
            and investors, leading to improved brand reputation and a stronger
            bottom line.
          </p>
          <h2>Governance</h2>
          <p>
            The governance factor evaluates the company's governance structures,
            ethical standards, transparency and accountability. Companies that
            do not embrace sound governance practices such as ethical
            leadership, regulatory compliance and stakeholder engagement can
            face negative publicity, lawsuits, and boycotts. This can lead to
            reputational damage, loss of sales, and increased costs associated
            with legal fees, remediation, and monitoring. On the other hand,
            companies that prioritise good governance can build trust with
            customers, employees, and investors, leading to improved brand
            reputation and a stronger bottom line.
          </p>
          <p>
            Overall, ESG factors can affect a company's bottom line in both
            positive and negative ways. By addressing these factors and
            integrating them into their business strategies, companies can
            improve their long-term sustainability and profitability. By
            understanding the ESG practices and performance of companies,
            investors are able to allocate their capital better by using
            industry-specific evaluations of ESG risks and opportunities, along
            with forward-looking impact scenarios.
          </p>
          <h2>The UN Principles for Responsible Investment (UN PRI)</h2>
          <p>
            The UN PRI is an international network of investors who are
            committed to incorporating ESG factors into their investment
            practices. Its goal is to encourage responsible investment and
            support sustainable financial markets. The UN PRI works with its
            signatories to develop and promote best practices in ESG investing,
            including integrating ESG factors into investment analysis and
            decision-making, engaging with companies to improve their ESG
            performance, and being transparent about ESG-related activities. By
            providing a framework for responsible investment, the UN PRI is
            playing a significant role in shifting investment portfolios towards
            considering ESG factors, ultimately leading to more sustainable and
            socially responsible investing practices.
          </p>
          <h2>Different types of ESG investing</h2>
          <p>
            Investors who prioritise ESG factors believe that companies that
            have good ESG practices are more likely to be sustainable in the
            long run. Investors also believe such companies are less likely to
            be exposed to reputational and regulatory risks. In addition, ESG
            investing allows investors to align the values of investee companies
            with their own financial goals and promote societal wellbeing. As
            such, ESG investing can lead to better returns and reduced risk by
            identifying companies that are well-positioned to thrive in a
            changing world.
          </p>
          <p>There are several types of ESG investing. These include:</p>
          <ol>
            <li>
              <p>
                <b>Screening:</b>Investors exclude companies or industries that
                do not meet their specific ESG criteria. This can be done
                through negative screening, where companies that engage in
                activities such as fossil fuel production, tobacco, or weapons
                are excluded. Positive screening involves investing in companies
                that have strong ESG practices or those that provide solutions
                to social or environmental problems.
              </p>
            </li>
            <li>
              <p>
                <b>Integration:</b>This involves incorporating ESG factors into
                traditional financial analysis to identify risks and
                opportunities. Investors use ESG data to analyse the financial
                performance of companies, taking into account factors such as
                regulatory risks, climate change, and social impact.
              </p>
            </li>
            <li>
              <p>
                <b>Impact Investing: </b>
              </p>
              This involves investing in companies or projects that have a
              specific social or environmental impact. The goal of impact
              investing is to generate both financial returns and measurable
              social or environmental impacts.
            </li>
            <li>
              <p>
                <b>Shareholder Activism: </b>Shareholders use their voting power
                to engage with companies on ESG issues. This can involve filing
                shareholder resolutions, attending annual meetings, and engaging
                with management to push for changes in corporate behaviour.
              </p>
            </li>
            <li>
              <p>
                <b>Thematic Investing: </b>This involves investing in companies
                that are aligned with a particular theme, such as renewable
                energy, gender diversity, or sustainable agriculture. Thematic
                investing allows investors to target specific ESG issues and
                invest in companies that are making a positive impact in those
                areas.
              </p>
            </li>
          </ol>
          <h2>ESG at SAB</h2>
          <p>
            Financial institutions such as investment banks, retail and
            commercial banks, as well as insurance companies, bear both the
            influence and responsibility to impact transformative change in the
            industry. The emergence of ESG aspirations among investors therefore
            represents a significant opportunity to structure portfolios that
            deliver financial value while impacting positively on society at
            large. This is true for SAB - الأول, given its status as one of the
            largest financial institutions in Saudi Arabia and its partnership
            with HSBC, a bank with vast global experience on ESG investing.
          </p>
          <p>
            As part of its commitment to responsible investing and
            sustainability, SAB - الأول has introduced a Green Deposit Account.
            This is an innovative Shariah compliant time deposit that offers
            customers the opportunity to fulfil their personal sustainability
            goals through investing funds to support financing of
            environmentally friendly projects in Saudi Arabia, with attractive
            returns in the short to medium term. Funds contributed are used to
            fund of a range of projects linked to various areas, including
            renewable energy, energy and water efficiency, sustainable land use
            and clean transportation.
          </p>
          <p>
            In recognition of its efforts, in 2023, SAB - الأول was recognised
            as the “Best Bank for Sustainable Finance in Saudi Arabia” for the
            year 2022 by Global Finance magazine, as part of its annual
            Excellence Awards. The award honours the best financial institutions
            globally. SAB - الأول was chosen for this award based on a set of
            international standards in a variety of categories, including
            sustainable financing, community support, emerging market growth,
            and infrastructure.
          </p>
        </>
      ,
      image: {
        name: "ESG-Investing.jpg",
        alt: "Image two description",
      },
    },
    {
      title:
        "Understanding the evolution of ESG: from niche to mainstream practice",
      shortDescription:
        "We live in a rapidly changing world in which climate and socio-economic risks pose a threat to societal wellbeing and the sustainability of the planet.",
      content: (
        <>
          <p>
            We live in a rapidly changing world in which climate and
            socio-economic risks pose a threat to societal wellbeing and the
            sustainability of the planet. These risks have contributed to a
            heightened awareness among investors and providers of financial
            capital of the relationship between environmental, social and
            corporate governance (ESG) considerations and long-term value
            creation. ESG refers to a set of criteria that investors use to
            evaluate a company's sustainability and ethical practices.{" "}
          </p>
          <p>
            The urgency of the climate and socio-economic change risks facing
            the world has led to a proliferation of policy and regulations
            targeting sustainability issues. The world has also seen notable
            growth in the number of signatories to industry-led initiatives such
            as the Principles for Responsible Investing (PRI) and United Nations
            Global Compact (UNGC). In 2022, the number of signatories to the PRI
            increased by 1 069 new signings, bringing the total to 4 902 members
            with an estimated total assets-under-management of US$121.3
            trillion. These and other developments reflect the growing
            importance of ESG issues worldwide. By considering ESG factors in
            their investment decisions, investors can promote companies that are
            committed to sustainable practices and are better equipped to manage
            risks and opportunities associated with environmental and social
            issues.
          </p>
          <p>
            Socially responsible investing, which preceded the concept of ESG,
            can be traced back to the 1960s. Initially, companies, universities
            and religious groups such as the Methodists and Quakers adopted
            ethical criteria to identify and exclude companies from their
            portfolios that did not complement their moral values. The
            exclusions targeted companies that were involved in the slave trade,
            tobacco and weapons manufacturing. However, there were no clear
            metrics or standards in place to monitor or measure sustainability
            across sectors or regions. The disastrous 1989 Exxon Valdez oil
            spill off Alaska, which caused widespread environmental damage,
            sharply heightened media and public awareness of the major negative
            impacts that day-to-day commerce can have on the planet. With each
            consequent environmental mishap now attracting widespread attention,
            investors and environmentalists eventually collaborated to launch
            the GRI in 1997.
          </p>
          <p>
            The GRI was introduced with the aim of helping businesses and other
            organisations take responsibility for their impacts, by providing
            them with the global common language to communicate those impacts.
            The framework remains an important standard for companies to manage
            and measure their sustainability practices and report on their
            performance.
          </p>
          <p>
            PRI stands for Principles for Responsible Investment. It is an
            international network of investors who commit to follow six
            voluntary principles on ESG integration, based on the idea that ESG
            factors can affect the long-term performance of investment
            portfolios. PRI also offers resources, events and collaboration
            opportunities to help investors implement the principles and advance
            responsible investment.
          </p>
          <p>
            ESG was first introduced as a concept by a 2005 United Nations in a
            report titled “Who Cares Wins”. This report was presented at a
            conference that brought together institutional investors, asset
            managers, research analysts, global consultants and government
            bodies and regulators to examine the role of ESG value drivers in
            the global economy. There was agreement among participants that ESG
            factors play an important role in driving sustainability.
          </p>
          ,
          <p>
            The adoption of ESG criteria by institutional investors gained
            further momentum in the wake of the global financial crisis of 2008.
            Studies showed that companies with better ESG scores tended to have
            lower risk profiles, higher profitability, and better long-term
            performance. In this context, investors became more risk-conscious
            and began to recognise the financial benefits of companies with
            strong ESG practices.
          </p>
          <p>
            In today’s world, ESG factors have become crucially important in
            investment decision-making and are attracting increasingly large
            investment flows.
          </p>
          <div className="space-50"></div>
          <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>The value of sustainable assets under management (AUM)*</th>
              </tr>
            </thead>
          </table>
          <table>
            <tbody>
              <tr>
                <td>USD 35.3 in 2020</td>
                <td>36%</td>
                <td>2025 expectation:</td>
              </tr>
              <tr>
                <td>(2016: USD 22.9) </td>
                <td>of global total AUM</td>
                <td>&lt;USD 50 trillion</td>
              </tr>
            </tbody>
          </table>
          </div>
          <span>*According to Statista</span>
          <p className="space-50"></p>
          <p>
            This represents a massive and fast ramping up swing from traditional
            to ESG investing, driven by 76% of consumers saying they will stop
            buying from companies that treat the environment, employees, or the
            community in which they operate poorly (PwC). ESG factors are used
            to assess both the positive and negative environmental and social
            impacts of a company thus enabling capital providers to assess the
            likelihood of that company to create value in different time
            horizons. ESG factors are not only considered in investment
            decision-making but also in company operations and strategy
            development. Companies that fail to prioritise ESG risk being left
            behind in rapidly evolving economies in which sustainability and
            social responsibility are becoming increasingly critical.
          </p>
          <p>
            From 2023 SAB - الأول has commenced engaging the UNEP FI, UN Global
            Compact and PRI.
          </p>
          <p>
            United Nations Environment Programme Finance Initiative (UNEP FI) is
            a partnership between UNEP and more than 300 financial institutions
            around the world, including banks, insurers and investors. It aims
            to align the financial sector with the UN Sustainable Development
            Goals and the Paris Agreement. It provides guidance, tools and
            training to help financial institutions integrate environmental,
            social and governance (ESG) factors into their decision-making and
            reporting. SAB - الأول is the first bank in Saudi Arabia to become
            an official Signatory of the UN Principles for Responsible Banking –
            a single framework for a sustainable banking industry developed
            through a partnership between banks worldwide and the UNEP FI.
          </p>
          <p>
            The UNGC is a call to companies everywhere to align their operations
            and strategies with ten universally accepted principles in the areas
            of human rights, labour, environment, and anti-corruption, and to
            take action in support of UN goals and issues embodied in the
            Sustainable Development Goals (SDGs). Launched in 2000, the UNGC is
            the largest corporate sustainability initiative in the world, with
            more than 15,000 companies and 3,000 non-business signatories based
            in over 160 countries, and more than 70 Local Networks. SAB - الأول
            joins the UNGC Saudi Chapter which has been active for several
            years. The is an elite list of 50 companies that have signed up to
            the UNGC Saudi Chapter including Ma’aden, SABIC, KAUST, Tadawul, STC
            and ACWA to name a few.
          </p>
          <p>
            The UNEP FI, UNGC and PRI programmes are linked by a common vision
            of a more sustainable financial system that supports positive social
            and environmental outcomes. These three initiatives collaborate on
            varied projects, including the Net-Zero Asset Owner Alliance, the
            Portfolio Alignment Team and the Sustainable Stock Exchanges
            initiative.
          </p>
          <p>
            In conclusion, ESG has evolved significantly in the years since it
            was first introduced, moving from a niche investing approach into a
            mainstream practice that is gaining importance in the business
            community. Going forward, experts expect ESG to continue playing a
            critical role in the investment landscape, shaping company
            decision-making and driving positive change for the environment and
            society.{" "}
          </p>
        </>
      ),
      image: {
        name: "Understanding-the-evolution-of-ESG-from-niche-to-mainstream-practice.jpg",
        alt: "Image two description",
      },
    },
    {
      title: "Why is sustainability important?",
      shortDescription:
        "Sustainability, a fundamental concept in today's world, encompasses the harmonious coexistence of social, environmental, and economic factors.",
      content: (
        <>
          <p>
            Sustainability, a fundamental concept in today's world, encompasses
            the harmonious coexistence of social, environmental, and economic
            factors. At its core lies the Triple Bottom Line (TBL), a framework
            that evaluates the success of organisations based on their
            performance in three dimensions: people, planet, and profit. The TBL
            recognises that a company's responsibilities extend beyond financial
            gains and emphasise the importance of social equity, ecological
            stewardship, and economic viability. By considering the social and
            environmental impacts of their actions alongside financial outcomes,
            organisations can make informed decisions that benefit society,
            preserve natural resources, and generate long-term economic
            prosperity. The Triple Bottom Line provides a holistic approach to
            sustainable development, encouraging businesses to pursue strategies
            that prioritise the well-being of people and the planet, ultimately
            leading to a more balanced and resilient future.
          </p>
          <p>
            In today’s ever-evolving world, it is increasingly apparent that
            embedding sustainability into corporate strategy and daily
            operations is no longer an option - it is now vital to long-term
            success. Although the concept of ‘sustainability’ may appear to be
            altruistic to some, it has become closely interlinked with the
            ability of businesses to create financial value over time. Many
            experts agree that doing well and doing good are intertwined,
            arguing that sustainable business models tend to be more profitable
            in the long term. Experts also argue that businesses need to do well
            financially in order to do good in the world. This suggests that
            sustainability initiatives can actually help to improve financial
            performance, and that developing a corporate culture of
            sustainability may be a source of competitive advantage for a
            company in the long run. Additionally, there is evidence that being
            more efficient at using resources is a strong indicator of superior
            financial performance overall.
          </p>
          <h2>The relationship between ESG and sustainability</h2>
          <p>
            Environmental, Social, and Governance (ESG), refers to a set of
            criteria that investors use to evaluate a company's sustainability
            and ethical practices. ESG factors are closely linked to
            sustainability because they focus on evaluating a company's impact
            on the environment, society, and its governance practices. By
            considering ESG factors in their investment decisions, investors can
            promote companies that are committed to sustainable practices and
            are better equipped to manage risks and opportunities associated
            with environmental and social issues. The relationship between ESG
            and sustainability is illustrated in the following diagram.
          </p>
          <img
            src={require(`../../../assets/images/insights/plan-1.png`)}
            alt={"ESG"}
          />
          <h2>
            Embedding sustainability can translate into long-term benefits
          </h2>
          <p>
            While sustainability strategies might be an investment in the short
            term, they can lead to long-term benefits. ESG metrics are often
            used to determine how responsible and sustainable an organisation
            is. According to McKinsey, companies with high ESG ratings
            consistently outperform the market in both the medium and long term.{" "}
          </p>
          <p>
            Environment sustainability is an integral component of
            sustainability. Sustainable practices reduce exposure to different
            sources of pollution and ensure responsible resource usage. Climate
            change refers to long term shifts in temperature and weather
            patterns, largely driven by human activities. By using renewable
            energy and reducing waste, businesses can save on costs, which
            translates to higher profits. Additionally, consumers are becoming
            increasingly aware of the environmental impact of the products they
            buy and are more likely to support sustainable companies, driving
            their growth and profitability.
          </p>
          <p>
            Social sustainability is also important for creating a just and
            equitable society. Sustainable practices help to improve the lives
            of individuals and communities, ensuring that resources are shared
            fairly, and everyone has access to essential services such as clean
            water, electricity, healthcare, and education. In addition, social
            sustainability ensures business continuity through commitments of
            customers to businesses and brands that are aligned with their own
            values.
          </p>
          <h2>
            Banks must contribute through sustainable financing through ESG
            investing
          </h2>
          <p>
            Banks play a critical role in financing economic activities and
            investments that can have a significant impact on the environment
            and society. As such, it is crucial for banks to provide sustainable
            financing that supports projects and initiatives that align with
            sustainability principles. Sustainable financing promotes
            responsible business practices. This helps to address urgent global
            challenges such as climate change, social inequality, and
            environmental degradation, banks can contribute to a more
            sustainable future and reduce their exposure to environmental and
            social risks that can have a significant impact on their financial
            performance. Additionally, ESG investing can help banks to attract a
            growing base of investors and customers who prioritise
            sustainability and ethical practices in their investment decisions.
            This is also in line with Article 2.1 (c)of the Paris Agreement
            which establishes the goal of making financial flows consistent with
            a pathway towards low greenhouse gas emissions and climate resilient
            development.
          </p>
          <h2>
            Aligning with Saudi Arabia’s Vision 2030, United Nations standards
            and international best practice
          </h2>
          <p>
            Sustainability is a critical component of Saudi Arabia’s Vision
            2030, a comprehensive roadmap which sets out ambitions for the
            Kingdom’s economic, social, and environmental development over the
            next decade. Sustainability, in this context, means balancing
            economic growth with environmental protection and social
            development. The plan includes a range of initiatives aimed at
            promoting sustainable practices, such as expanding renewable energy
            sources, reducing greenhouse gas emissions, and investing in
            sustainable transport infrastructure. Additionally, Vision 2030
            seeks to promote social inclusion and gender equality, improve
            healthcare and education, and preserve the country's cultural
            heritage. Overall, sustainability is a central pillar of Vision 2030
            and reflects the country's commitment to building a more resilient,
            sustainable, and prosperous future for its citizens and the world.
            This informs our ESG strategy at SAB.
          </p>
          <p>
            Our commitment to sustainability is also seen in our support for the
            United Nations Sustainable Development Goals (SDGs) and the United
            Nations Framework Convention on Climate Change (UNFCCC) agreement.
            The SDGs provide a global blueprint for sustainable development and
            include 17 interconnected goals, such as ending poverty and hunger,
            promoting clean energy, and reducing inequality. Similarly, the
            UNFCCC agreement seeks to limit global warming to below 2 degrees
            Celsius and to pursue efforts to limit it to 1.5 degrees Celsius.
          </p>
          <p>
            We are taking concrete steps to reduce our carbon footprint and
            promote sustainable practices. For example, SAB led the
            establishment of the Saudi banks’ ESG working group under the
            guidance of the Saudi Central Bank (SAMA). SAB was also the first
            Saudi Bank to achieve zero single-use non-recycled plastic bottles
            across offices, and the first Saudi bank to issue a green loan in
            the Kingdom. In the year ahead, SAB will continue expanding our
            sustainable finance products and services, while ensuring that all
            our employees receive appropriate and applicable ESG training and
            development. Further, the bank will continue integrating ESG into
            existing governance and risk management frameworks. We will remain
            focused on ensuring that all external stakeholders are kept informed
            of SAB’s progress on ESG and other non-financial issues disclosures.
          </p>
        </>
      ),
      image: {
        name: "why-is-sustainbaility-important.jpg",
        alt: "Image one description",
      },
    },

  ],
};

export default insightPageData;
